import {
  MemberBulkButtonComponents,
  MemberTableBulkName,
} from "@components/common/faro-table/faro-table-types";
import { BulkChangeCompanyRole } from "@components/table/members/bulk-actions/bulk-change-company-role";
import { BulkExportMembers } from "@components/table/members/bulk-actions/bulk-export-members";
import { BulkRemoveMember } from "@components/table/members/bulk-actions/bulk-remove-member";
import { BulkResendInvitation } from "@components/table/members/bulk-actions/bulk-resend-invitation";
import { BulkSendEmail } from "@components/table/members/bulk-actions/bulk-send-email";
import { BaseMembersProps } from "@custom-types/member-types";
import { SdbCompany } from "@custom-types/sdb-company-types";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useShouldShowBulkAction } from "@hooks/table/use-should-show-bulk-action";
import { useAppSelector } from "@store/store-helper";
import { fetchingMembersFlagsSelector } from "@store/members/members-selector";
import { selectedEntitiesSelector } from "@store/table/table-selector";
import { isMemberActive } from "@utils/user-utils";
import { useMemo } from "react";

interface Props extends BaseMembersProps {
  /** The details of the selected company in which bulk actions are shown */
  company: SdbCompany | null;
}

/** List all the bulk action components for company member table */
export function useCompanyMemberBulkActions({
  members,
  company,
}: Props): MemberBulkButtonComponents {
  const selectedEntities = useAppSelector(selectedEntitiesSelector("members"));
  const { isFetchingCompanyMembers } = useAppSelector(
    fetchingMembersFlagsSelector
  );
  const isAnyRowSelected = selectedEntities.length !== 0;
  const isAnyMemberPending = selectedEntities.some(
    (member) => !isMemberActive(member)
  );
  const {
    canEditMember,
    canDeleteMemberFromCompany,
    canResendInvitationToCompany,
  } = useHasUserValidRoleCompanyLevel();

  const allowedButtonsBasedOnRowSelection: MemberTableBulkName[] =
    useMemo(() => {
      const allowedButtons: MemberTableBulkName[] = ["export"];

      if (isAnyRowSelected) {
        allowedButtons.push("sendEmail");

        if (canEditMember) {
          allowedButtons.push("changeRole");
        }

        if (isAnyMemberPending && canResendInvitationToCompany) {
          allowedButtons.push("resendInvitation");
        }

        if (canDeleteMemberFromCompany) {
          allowedButtons.push("removeMember");
        }
      }

      return allowedButtons;
    }, [
      canDeleteMemberFromCompany,
      canEditMember,
      isAnyMemberPending,
      isAnyRowSelected,
      canResendInvitationToCompany,
    ]);

  const { shouldShowBulkButtons } = useShouldShowBulkAction({
    allowedButtonsBasedOnRowSelection,
  });

  /** Determine whether to show the export button */
  const shouldShowExportButton =
    shouldShowBulkButtons("export") &&
    (isFetchingCompanyMembers || members.length > 0);

  if (!company) {
    return {
      changeRole: null,
      resendInvitation: null,
      removeMember: null,
      sendEmail: null,
      export: null,
    };
  }

  return {
    changeRole: shouldShowBulkButtons("changeRole") ? (
      <BulkChangeCompanyRole companyId={company.id} entityType="members" />
    ) : null,

    resendInvitation: shouldShowBulkButtons("resendInvitation") ? (
      <BulkResendInvitation
        selectedEntities={selectedEntities}
        companyId={company.id}
        subjectType="workspace"
      />
    ) : null,

    removeMember: shouldShowBulkButtons("removeMember") ? (
      <BulkRemoveMember subjectType="workspace" companyId={company.id} />
    ) : null,

    sendEmail: shouldShowBulkButtons("sendEmail") ? (
      <BulkSendEmail selectedEntities={selectedEntities} />
    ) : null,

    export: shouldShowExportButton ? (
      <BulkExportMembers
        selectedEntities={isAnyRowSelected ? selectedEntities : members}
        subjectType="workspace"
        entityName={company.name}
        areEntitiesSelected={isAnyRowSelected}
      />
    ) : null,
  };
}
